import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ShoppingBagIcon,
    BanknotesIcon,
    ClockIcon,
    UserGroupIcon,
    PlusIcon,
    LinkIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ArrowTrendingUpIcon,
    ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getOrders, getMembers } from '@services/admin.service';
import { Order, MemberUser } from '@utilities/types';
import { NavigationPathsEnum } from '@utilities/enums';

const PRIMARY = '#16A34A';
const PALETTE = ['#16A34A', '#0EA5E9', '#F59E0B', '#EF4444', '#8B5CF6', '#64748B'];

const STATUS_COLORS: Record<string, string> = {
    paid: '#10B981',
    pending: '#F59E0B',
    deferred: '#6366F1',
    failed: '#EF4444',
    cancelled: '#94A3B8',
};

const STATUS_LABELS: Record<string, string> = {
    paid: 'Payée',
    pending: 'En attente',
    deferred: 'Différé',
    failed: 'Échec',
    cancelled: 'Annulé',
};

function formatMoney(amount: number, currency = 'EUR') {
    const symbol = currency.toUpperCase() === 'EUR' ? '€' : currency.toUpperCase();
    return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${symbol}`;
}

function formatRelative(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `il y a ${days} j`;
    return new Date(iso).toLocaleDateString('fr-FR');
}

export const Dashboard: FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [members, setMembers] = useState<MemberUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [o, m] = await Promise.all([getOrders(), getMembers()]);
            setOrders(o.data);
            setMembers(m.data);
        } catch (err: any) {
            setError(err.message || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const metrics = useMemo(() => {
        const paid = orders.filter((o) => o.paymentStatus === 'paid');
        const pending = orders.filter((o) => o.paymentStatus === 'pending' || o.paymentStatus === 'deferred');
        const totalRevenue = paid.reduce((sum, o) => sum + (o.amountTotal || 0), 0);
        const pendingRevenue = pending.reduce((sum, o) => sum + (o.amountTotal || 0), 0);
        const now = Date.now();
        const last30 = orders.filter((o) => now - new Date(o.createdAt).getTime() < 30 * 86400000);
        const prev30 = orders.filter((o) => {
            const d = now - new Date(o.createdAt).getTime();
            return d >= 30 * 86400000 && d < 60 * 86400000;
        });
        const last30Revenue = last30
            .filter((o) => o.paymentStatus === 'paid')
            .reduce((s, o) => s + (o.amountTotal || 0), 0);
        const prev30Revenue = prev30
            .filter((o) => o.paymentStatus === 'paid')
            .reduce((s, o) => s + (o.amountTotal || 0), 0);
        const revenueDelta = prev30Revenue > 0
            ? ((last30Revenue - prev30Revenue) / prev30Revenue) * 100
            : (last30Revenue > 0 ? 100 : 0);
        const ordersDelta = prev30.length > 0
            ? ((last30.length - prev30.length) / prev30.length) * 100
            : (last30.length > 0 ? 100 : 0);
        const activeMembers = new Set(last30.map((o) => o.memberId)).size;

        return {
            totalOrders: orders.length,
            ordersLast30: last30.length,
            ordersDelta,
            paidOrders: paid.length,
            totalRevenue,
            last30Revenue,
            revenueDelta,
            pendingCount: pending.length,
            pendingRevenue,
            totalMembers: members.length,
            activeMembers,
        };
    }, [orders, members]);

    const notifications = useMemo(() => {
        const list: { type: 'warning' | 'info' | 'danger'; title: string; detail: string; href: string }[] = [];
        const now = Date.now();
        const stalePending = orders.filter((o) => {
            const isPending = o.paymentStatus === 'pending' || o.paymentStatus === 'deferred';
            return isPending && now - new Date(o.createdAt).getTime() > 3 * 86400000;
        });
        if (stalePending.length > 0) {
            list.push({
                type: 'warning',
                title: `${stalePending.length} commande${stalePending.length > 1 ? 's' : ''} en attente depuis +3 jours`,
                detail: 'Pensez à relancer ou générer un lien de paiement.',
                href: NavigationPathsEnum.PAYMENTS,
            });
        }
        const failed = orders.filter((o) => o.paymentStatus === 'failed');
        if (failed.length > 0) {
            list.push({
                type: 'danger',
                title: `${failed.length} paiement${failed.length > 1 ? 's' : ''} en échec`,
                detail: 'Vérifiez ces commandes et contactez les membres concernés.',
                href: NavigationPathsEnum.ORDERS,
            });
        }
        const today = orders.filter((o) => now - new Date(o.createdAt).getTime() < 86400000);
        if (today.length > 0) {
            list.push({
                type: 'info',
                title: `${today.length} nouvelle${today.length > 1 ? 's' : ''} commande${today.length > 1 ? 's' : ''} aujourd'hui`,
                detail: 'Consultez les nouvelles commandes du jour.',
                href: NavigationPathsEnum.ORDERS,
            });
        }
        return list;
    }, [orders]);

    const monthly = useMemo(() => {
        const months: { label: string; key: string; count: number; revenue: number }[] = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            months.push({
                key,
                label: d.toLocaleDateString('fr-FR', { month: 'short' }),
                count: 0,
                revenue: 0,
            });
        }
        orders.forEach((o) => {
            const d = new Date(o.createdAt);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const m = months.find((mm) => mm.key === key);
            if (!m) return;
            m.count += 1;
            if (o.paymentStatus === 'paid') m.revenue += o.amountTotal || 0;
        });
        return months;
    }, [orders]);

    const paymentBreakdown = useMemo(() => {
        const map = new Map<string, number>();
        orders.forEach((o) => {
            const k = o.paymentStatus || 'pending';
            map.set(k, (map.get(k) || 0) + 1);
        });
        return Array.from(map.entries()).map(([key, value]) => ({ key, value }));
    }, [orders]);

    const topMembers = useMemo(() => {
        const map = new Map<string, { id: string; label: string; revenue: number; count: number }>();
        orders.forEach((o) => {
            if (o.paymentStatus !== 'paid') return;
            const id = o.memberId || o.memberEmail || 'unknown';
            const label = o.member
                ? `${o.member.firstName} ${o.member.lastName}`.trim() || o.member.email
                : (o.memberEmail || 'Membre inconnu');
            const cur = map.get(id) || { id, label, revenue: 0, count: 0 };
            cur.revenue += o.amountTotal || 0;
            cur.count += 1;
            map.set(id, cur);
        });
        return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
    }, [orders]);

    const recentOrders = useMemo(
        () => [...orders]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .slice(0, 6),
        [orders],
    );

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-screen">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Vue d'ensemble de l'activité — {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KpiCard
                        icon={<BanknotesIcon className="w-5 h-5" />}
                        label="Chiffre d'affaires (30j)"
                        value={formatMoney(metrics.last30Revenue)}
                        subtitle={`Total : ${formatMoney(metrics.totalRevenue)}`}
                        delta={metrics.revenueDelta}
                        accent="emerald"
                    />
                    <KpiCard
                        icon={<ShoppingBagIcon className="w-5 h-5" />}
                        label="Commandes (30j)"
                        value={String(metrics.ordersLast30)}
                        subtitle={`Total : ${metrics.totalOrders}`}
                        delta={metrics.ordersDelta}
                        accent="sky"
                    />
                    <KpiCard
                        icon={<ClockIcon className="w-5 h-5" />}
                        label="Paiements en attente"
                        value={String(metrics.pendingCount)}
                        subtitle={`${formatMoney(metrics.pendingRevenue)} à encaisser`}
                        accent="amber"
                    />
                    <KpiCard
                        icon={<UserGroupIcon className="w-5 h-5" />}
                        label="Membres actifs (30j)"
                        value={`${metrics.activeMembers} / ${metrics.totalMembers}`}
                        subtitle={`${metrics.totalMembers} membres au total`}
                        accent="violet"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
                            <span className="text-xs text-gray-400">{notifications.length} actives</span>
                        </div>
                        {notifications.length === 0 ? (
                            <div className="flex items-center gap-3 py-6 text-sm text-gray-500">
                                <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                                Tout est à jour, aucune action requise.
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {notifications.map((n, i) => (
                                    <li key={i}>
                                        <Link
                                            to={n.href}
                                            className={`flex items-start gap-3 p-3 rounded-lg border transition-colors hover:bg-gray-50 ${n.type === 'danger' ? 'border-red-100 bg-red-50/50'
                                                    : n.type === 'warning' ? 'border-amber-100 bg-amber-50/50'
                                                        : 'border-sky-100 bg-sky-50/50'
                                                }`}
                                        >
                                            <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 shrink-0 ${n.type === 'danger' ? 'text-red-500'
                                                    : n.type === 'warning' ? 'text-amber-500'
                                                        : 'text-sky-500'
                                                }`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900">{n.title}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{n.detail}</p>
                                            </div>
                                            <ArrowRightIcon className="w-4 h-4 text-gray-300 mt-1" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4">Actions rapides</h2>
                        <div className="grid grid-cols-2 gap-2">
                            <QuickAction to={NavigationPathsEnum.MEMBERS} icon={<PlusIcon className="w-4 h-4" />} label="Inviter un membre" />
                            <QuickAction to={NavigationPathsEnum.PAYMENTS} icon={<LinkIcon className="w-4 h-4" />} label="Liens de paiement" />
                            <QuickAction to={NavigationPathsEnum.ORDERS} icon={<ShoppingBagIcon className="w-4 h-4" />} label="Voir les commandes" />
                            <QuickAction to={NavigationPathsEnum.SETTINGS_PRICING} icon={<BanknotesIcon className="w-4 h-4" />} label="Tarification" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-900">Activité (12 derniers mois)</h2>
                                <p className="text-xs text-gray-400 mt-0.5">Commandes créées et CA encaissé par mois</p>
                            </div>
                        </div>
                        <MonthlyChart data={monthly} />
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4">Répartition des paiements</h2>
                        <DonutChart data={paymentBreakdown} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-semibold text-gray-900">Top 5 membres (CA)</h2>
                            <Link to={NavigationPathsEnum.MEMBERS} className="text-xs text-primary hover:underline">
                                Voir tout
                            </Link>
                        </div>
                        {topMembers.length === 0 ? (
                            <p className="text-sm text-gray-400 py-4">Aucune commande payée pour le moment.</p>
                        ) : (
                            <ul className="space-y-3">
                                {topMembers.map((m, i) => {
                                    const max = topMembers[0].revenue || 1;
                                    const ratio = (m.revenue / max) * 100;
                                    return (
                                        <li key={m.id}>
                                            <div className="flex items-center justify-between text-sm mb-1">
                                                <span className="font-medium text-gray-900 truncate">
                                                    <span className="text-gray-400 mr-2">#{i + 1}</span>
                                                    {m.label}
                                                </span>
                                                <span className="text-gray-600 tabular-nums">{formatMoney(m.revenue)}</span>
                                            </div>
                                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full transition-all"
                                                    style={{ width: `${ratio}%`, background: PALETTE[i % PALETTE.length] }}
                                                />
                                            </div>
                                            <div className="text-[11px] text-gray-400 mt-0.5">{m.count} commande{m.count > 1 ? 's' : ''}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-semibold text-gray-900">Commandes récentes</h2>
                            <Link to={NavigationPathsEnum.ORDERS} className="text-xs text-primary hover:underline">
                                Voir tout
                            </Link>
                        </div>
                        {recentOrders.length === 0 ? (
                            <p className="text-sm text-gray-400 py-4">Aucune commande pour le moment.</p>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {recentOrders.map((o) => {
                                    const status = o.paymentStatus || 'pending';
                                    return (
                                        <li key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {o.client ? `${o.client.firstName} ${o.client.lastName}` : 'Client inconnu'}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {o.member ? `${o.member.firstName} ${o.member.lastName}` : o.memberEmail} · {formatRelative(o.createdAt)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold tabular-nums">
                                                    {typeof o.amountTotal === 'number' ? formatMoney(o.amountTotal, o.currency) : '—'}
                                                </div>
                                                <span
                                                    className="inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium"
                                                    style={{ background: `${STATUS_COLORS[status]}20`, color: STATUS_COLORS[status] }}
                                                >
                                                    {STATUS_LABELS[status] || status}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

interface KpiCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    subtitle?: string;
    delta?: number;
    accent: 'emerald' | 'sky' | 'amber' | 'violet';
}
const ACCENT_BG: Record<KpiCardProps['accent'], string> = {
    emerald: 'bg-emerald-50 text-emerald-600',
    sky: 'bg-sky-50 text-sky-600',
    amber: 'bg-amber-50 text-amber-600',
    violet: 'bg-violet-50 text-violet-600',
};
const KpiCard: FC<KpiCardProps> = ({ icon, label, value, subtitle, delta, accent }) => (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${ACCENT_BG[accent]}`}>
                {icon}
            </span>
            {typeof delta === 'number' && Number.isFinite(delta) && (
                <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    <ArrowTrendingUpIcon className={`w-3.5 h-3.5 ${delta < 0 ? 'rotate-180' : ''}`} />
                    {Math.abs(delta).toFixed(0)}%
                </span>
            )}
        </div>
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
);

const QuickAction: FC<{ to: string; icon: React.ReactNode; label: string }> = ({ to, icon, label }) => (
    <Link
        to={to}
        className="flex flex-col items-start gap-2 p-3 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left"
    >
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary/10 text-primary">{icon}</span>
        <span className="text-xs font-medium text-gray-700">{label}</span>
    </Link>
);

interface MonthlyChartProps {
    data: { label: string; count: number; revenue: number }[];
}
const MonthlyChart: FC<MonthlyChartProps> = ({ data }) => {
    const maxCount = Math.max(1, ...data.map((d) => d.count));
    const maxRevenue = Math.max(1, ...data.map((d) => d.revenue));
    const width = 600;
    const height = 220;
    const padding = { top: 16, right: 16, bottom: 28, left: 32 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    const groupW = innerW / data.length;
    const barW = groupW * 0.55;

    const linePoints = data.map((d, i) => {
        const x = padding.left + groupW * i + groupW / 2;
        const y = padding.top + innerH - (d.revenue / maxRevenue) * innerH;
        return { x, y };
    });
    const linePath = linePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-400" /> Commandes</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> CA encaissé (€)</span>
            </div>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                {[0, 0.25, 0.5, 0.75, 1].map((r) => (
                    <line
                        key={r}
                        x1={padding.left}
                        x2={width - padding.right}
                        y1={padding.top + innerH * (1 - r)}
                        y2={padding.top + innerH * (1 - r)}
                        stroke="#F1F5F9"
                        strokeWidth={1}
                    />
                ))}
                {data.map((d, i) => {
                    const h = (d.count / maxCount) * innerH;
                    const x = padding.left + groupW * i + (groupW - barW) / 2;
                    const y = padding.top + innerH - h;
                    return (
                        <g key={i}>
                            <rect x={x} y={y} width={barW} height={h} rx={3} fill="#7DD3FC" />
                            <text
                                x={padding.left + groupW * i + groupW / 2}
                                y={height - 8}
                                textAnchor="middle"
                                fontSize="10"
                                fill="#94A3B8"
                            >
                                {d.label}
                            </text>
                        </g>
                    );
                })}
                <path d={linePath} fill="none" stroke={PRIMARY} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {linePoints.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={3} fill="#fff" stroke={PRIMARY} strokeWidth={2} />
                ))}
            </svg>
        </div>
    );
};

interface DonutChartProps {
    data: { key: string; value: number }[];
}
const DonutChart: FC<DonutChartProps> = ({ data }) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) {
        return <p className="text-sm text-gray-400 py-8 text-center">Aucune donnée à afficher.</p>;
    }
    const size = 180;
    const radius = 70;
    const stroke = 22;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;
    const segments = data.map((d) => {
        const ratio = d.value / total;
        const seg = {
            key: d.key,
            value: d.value,
            ratio,
            color: STATUS_COLORS[d.key] || '#94A3B8',
            dasharray: `${ratio * circumference} ${circumference}`,
            offset,
        };
        offset += ratio * circumference;
        return seg;
    });

    return (
        <div className="flex flex-col items-center gap-4">
            <div className="relative">
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
                    <g transform={`rotate(-90 ${cx} ${cy})`}>
                        {segments.map((s, i) => (
                            <circle
                                key={i}
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                stroke={s.color}
                                strokeWidth={stroke}
                                strokeDasharray={s.dasharray}
                                strokeDashoffset={-s.offset}
                                strokeLinecap="butt"
                            />
                        ))}
                    </g>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold tabular-nums">{total}</span>
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">Commandes</span>
                </div>
            </div>
            <ul className="w-full space-y-1.5">
                {segments.map((s) => (
                    <li key={s.key} className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                            <span className="text-gray-600">{STATUS_LABELS[s.key] || s.key}</span>
                        </span>
                        <span className="tabular-nums text-gray-900 font-medium">
                            {s.value} <span className="text-gray-400">({(s.ratio * 100).toFixed(0)}%)</span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
